import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { uniqBy } from "lodash";
import { Award, BadgeCheck, IdCard } from "lucide-react";
import { renderSettingsResourceLabel } from "../../../../../../../utils/helpers/settings";
import { useQualificationPreferences } from "../../../../hooks/useQualificationPreferences";

const FIELDS = [
    { field: "skills", icon: Award },
    { field: "licenses", icon: IdCard },
    { field: "certifications", icon: BadgeCheck },
] as const;

type Field = typeof FIELDS[number]["field"];

// Users hold licenses/certifications as { type, status }, sections hold the resource itself
const getUserItems = (user: any, field: Field) => {
    const items: any[] = user?.[field] ?? [];
    if (field === "skills") return items;
    return items.filter((item) => item?.status !== "expired").map((item) => item?.type);
};

interface QualificationsProps {
    sections?: any[];
    user?: any;
}

function Qualifications({ sections, user }: QualificationsProps) {
    const { t } = useTranslation();
    const enabled = useQualificationPreferences();

    const values = useMemo(() => FIELDS.filter(({ field }) => enabled[field]).map(({ field, icon }) => ({
        field,
        icon,
        items: uniqBy(
            (user ? getUserItems(user, field) : (sections ?? []).flatMap((section) => section?.[field] ?? []))
                .filter((item: any) => item && typeof item === "object"),
            (item: any) => item.id
        ),
    })).filter(({ items }) => items.length > 0), [sections, user, enabled]);

    return (
        <>
            {values.map(({ field, icon: Icon, items }) => (
                <div key={field} className="tw-flex tw-items-start tw-justify-between tw-gap-3 tw-mt-1">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-shrink-0">
                        <Icon size={19} className="tw-text-primary" />
                        <span className="tw-text-sm">{t(field)}</span>
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-justify-end tw-gap-1">
                        {items.map((item: any) => (
                            <span
                                key={item.id}
                                className="tw-bg-emerald-100 tw-px-3 tw-py-1 tw-rounded tw-text-sm tw-text-emerald-900 tw-font-semibold"
                            >
                                {renderSettingsResourceLabel(item)}
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </>
    );
}

export default Qualifications;
